import type { Seed } from "./types.js";

/**
 * Splitting a tie's entrants into two sides of comparable strength.
 *
 * This lived in SQL, as `auto_tie_teams`, and did a plain serpentine — A B B A down the
 * seeding order. Serpentine is a good opening move and a poor final answer: it takes no
 * account of the actual gaps between ratings, so a field with one runaway top seed and a
 * flat middle comes out lopsided in exactly the way an organiser notices at 8pm.
 *
 * Here instead, for the reason everything else about who-plays-whom is here: one
 * implementation, unit tested, rather than a second one in a language with no test suite
 * that has to be kept in step by hand.
 */

export interface TeamSplit {
  /** In seed order, strongest first. Index 0 is seed 1. */
  a: Seed[];
  b: Seed[];
  /**
   * The one left over from an odd entry list.
   *
   * Dropped rather than refused: a mirrored tie cannot halve an odd number, and an organiser
   * can see who is out and move somebody in. A button that does nothing until the numbers
   * happen to be even is a button that looks broken. It is always the last seed, so the
   * person left out is the one with least at stake in the seeding.
   */
  unplaced: Seed | null;
  /**
   * The gap between the two sides' total rating. Zero is a dead heat.
   *
   * Reported so the organiser can see what they are being handed rather than trusting it.
   * Only counts rated players — see `splitIntoTeams`.
   */
  gap: number;
}

/**
 * Serpentine, then swap until the totals stop improving.
 *
 * **The snake** (A B B A) is the starting point because it is already close and because it
 * is the arrangement an organiser can check by eye. **The hill climb** then tries every
 * single swap across the two sides, takes the best one, and repeats until nothing helps.
 * That is O(n²) per pass on rosters of a dozen, which is nothing, and it lands on a genuinely
 * even split rather than a plausible one.
 *
 * **Unrated players are spread by count, not by value.** They have no number to add up, and
 * substituting one — the field average, say — would state a strength the app does not know
 * and then balance around it. They sort last, so the snake deals them out alternately and
 * lands on the closest split parity allows: level with an even number of them, one apart
 * with an odd. Swaps that would move an unrated player across are then refused, so the
 * balancing cannot make that worse while chasing the totals.
 */
export function splitIntoTeams(seeds: Seed[]): TeamSplit {
  const even = seeds.length - (seeds.length % 2);
  const unplaced = even < seeds.length ? (seeds[seeds.length - 1] as Seed) : null;
  const field = seeds.slice(0, even);

  if (field.length < 2) return { a: [], b: [], unplaced, gap: 0 };

  // Serpentine. Each adjacent pair contributes one to each side, so the sides are equal for
  // any even field without having to check.
  const a: Seed[] = [];
  const b: Seed[] = [];
  field.forEach((seed, i) => {
    const toA = Math.floor(i / 2) % 2 === 0 ? i % 2 === 0 : i % 2 !== 0;
    (toA ? a : b).push(seed);
  });

  improve(a, b);

  return {
    a: bySeed(a),
    b: bySeed(b),
    unplaced,
    gap: Math.abs(total(a) - total(b)),
  };
}

/** Sum of the rated. The unrated contribute nothing, which is why they are counted instead. */
const total = (side: Seed[]): number => side.reduce((sum, seed) => sum + (seed.rating ?? 0), 0);

const unratedCount = (side: Seed[]): number => side.filter((seed) => seed.rating == null).length;

const bySeed = (side: Seed[]): Seed[] => [...side].sort((x, y) => x.seed - y.seed);

/**
 * Best-improving swap, repeatedly, until no single swap helps.
 *
 * A local optimum rather than the true optimum — finding the latter is number partitioning,
 * which is NP-hard, and the difference on a dozen club players is a rounding error against
 * the fact that nobody plays to their rating on a Tuesday. The bound exists so a pathological
 * field cannot spin: each pass strictly reduces the gap, so it cannot cycle, but a cap costs
 * nothing and says so out loud.
 */
function improve(a: Seed[], b: Seed[]): void {
  const evenlyUnrated = Math.abs(unratedCount(a) - unratedCount(b));

  for (let pass = 0; pass < 100; pass++) {
    let best: { i: number; j: number; gap: number } | null = null;
    const current = Math.abs(total(a) - total(b));

    for (let i = 0; i < a.length; i++) {
      for (let j = 0; j < b.length; j++) {
        const x = a[i] as Seed;
        const y = b[j] as Seed;

        // Swapping a rated player for an unrated one moves the headcount as well as the
        // total, and the headcount is the only handle there is on players with no number.
        if ((x.rating == null) !== (y.rating == null)) continue;

        const gap = Math.abs(
          total(a) -
            (x.rating ?? 0) +
            (y.rating ?? 0) -
            (total(b) - (y.rating ?? 0) + (x.rating ?? 0)),
        );
        if (gap < current && (best === null || gap < best.gap)) best = { i, j, gap };
      }
    }

    if (!best) break;
    const x = a[best.i] as Seed;
    a[best.i] = b[best.j] as Seed;
    b[best.j] = x;
  }

  // The swap filter above preserves it, so this is an assertion rather than a fix-up: if it
  // ever fires, the filter has been broken by an edit and the sides are no longer comparable.
  if (Math.abs(unratedCount(a) - unratedCount(b)) !== evenlyUnrated) {
    throw new Error("balancing moved an unrated player across sides");
  }
}
